<template>
    <div
        class="cropped-face bg-gray-100 overflow-hidden"
        :style="containerStyle"
    >
        <div v-if="!imageLoaded && !failed" class="w-full h-full animate-pulse bg-gray-200"></div>

        <div
            v-if="imageLoaded && bbox"
            class="w-full h-full"
            :style="cropStyle"
        ></div>

        <img
            v-else-if="imageLoaded && !bbox"
            :src="src"
            :alt="alt"
            class="w-full h-full object-cover"
        />

        <div
            v-if="failed"
            class="w-full h-full flex items-center justify-center text-gray-400 text-xs"
        >
            {{ alt }}
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType, CSSProperties} from "vue";
import {FaceBBox} from "@/types/FaceGroupingType";

export default defineComponent({
    name: "CroppedFaceImage",

    props: {
        src: {
            type: String,
            required: true,
        },
        bbox: {
            type: Object as PropType<FaceBBox | null>,
            default: null,
        },
        size: {
            type: Number,
            default: 100,
        },
        padding: {
            type: Number,
            default: 0.4,
        },
        alt: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            naturalWidth: 0,
            naturalHeight: 0,
            imageLoaded: false,
            failed: false,
        };
    },

    computed: {
        containerStyle(): CSSProperties {
            return {
                width: `${this.size}px`,
                height: `${this.size}px`,
                maxWidth: '100%',
            };
        },

        cropStyle(): CSSProperties {
            if (!this.bbox || !this.imageLoaded || this.naturalWidth === 0) {
                return {};
            }

            const nw = this.naturalWidth;
            const nh = this.naturalHeight;
            const b = this.bbox;

            const cx = b.x + b.w / 2;
            const cy = b.y + b.h / 2;

            let side = Math.max(b.w, b.h) * (1 + this.padding * 2);
            side = Math.min(side, nw, nh);

            let cropX = cx - side / 2;
            let cropY = cy - side / 2;
            cropX = Math.max(0, Math.min(cropX, nw - side));
            cropY = Math.max(0, Math.min(cropY, nh - side));

            const scale = this.size / side;

            return {
                backgroundImage: `url("${this.src}")`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: `${nw * scale}px ${nh * scale}px`,
                backgroundPosition: `-${cropX * scale}px -${cropY * scale}px`,
            };
        },
    },

    watch: {
        src: {
            immediate: true,
            handler() {
                this.loadImage();
            },
        },
    },

    methods: {
        loadImage() {
            this.imageLoaded = false;
            this.failed = false;
            this.naturalWidth = 0;
            this.naturalHeight = 0;

            if (!this.src) {
                this.failed = true;
                return;
            }

            const img = new Image();
            img.onload = () => {
                this.naturalWidth = img.naturalWidth;
                this.naturalHeight = img.naturalHeight;
                this.imageLoaded = true;
            };
            img.onerror = () => {
                this.failed = true;
            };
            img.src = this.src;
        },
    },
});
</script>
